import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { useLang } from '../LanguageContext';
import placeholder from '../assets/placeholder';
import { LABEL_MODES, getLabelLines, getLabelText } from '../hooks/useLabelMode';
import { copyText } from '../utils/clipboard';
import { authFetch } from '../utils/authFetch';

export default function StrainCard({
  strain,
  isAdmin = false,
  publicView = false,
  onEdit,
  onDelete,
  labelMode = LABEL_MODES.NAME_AND_NUMBER,
}) {
  const { t } = useLang();
  const [expanded, setExpanded] = useState(false);
  const [fullDesc, setFullDesc] = useState(false);
  const [cuttings, setCuttings] = useState([]);
  const [loadingCuttings, setLoadingCuttings] = useState(false);
  const token = localStorage.getItem('token');
  const decodeRole = (tok) => {
    if (!tok) return null;
    try {
      return JSON.parse(atob(tok.split('.')[1])).role;
    } catch {
      return null;
    }
  };
  const role = decodeRole(token);

  const numberValue = publicView ? strain.slug : (strain.slug || strain._id);
  const labelLines = getLabelLines(labelMode, strain.name, numberValue);
  const labelText = getLabelText(labelMode, strain.name, numberValue);

  const labelLineStyle = {
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  };

  const displayName = () => {
    if (labelMode === LABEL_MODES.NUMBER_ONLY) return numberValue;
    if (labelMode === LABEL_MODES.NAME_ONLY) return strain.name;
    if (strain.name && numberValue && strain.name !== numberValue) {
      return `${strain.name} (${numberValue})`;
    }
    return strain.name || numberValue;
  };
  const displayNameValue = displayName();

  const addCutting = async () => {
    const code = window.prompt(t('code'));
    if (!code) return;
    try {
      await authFetch('/api/admin/cuttings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, strainId: strain._id }),
      }, { token });
      setCuttings([]); // refetch on expand next time
      setExpanded(false);
    } catch (err) {
      console.error('Failed to add cutting', err);
    }
  };

  const toggleExpand = async () => {
    if (!expanded && isAdmin && cuttings.length === 0 && !loadingCuttings) {
      setLoadingCuttings(true);
      try {
        const res = await authFetch(`/api/strains/${strain.slug}/cuttings`, {}, { token });
        const data = await res.json();
        if (Array.isArray(data)) setCuttings(data);
      } catch (err) {
        console.error('Failed to fetch cuttings', err);
      } finally {
        setLoadingCuttings(false);
      }
    }
    setExpanded(!expanded);
  };

  const descriptionText = publicView ? strain.shortDescription : strain.description;
  const shortDesc = descriptionText && descriptionText.length > 120
    ? descriptionText.slice(0, 120) + '...'
    : descriptionText;

  const mainMedia =
    (Array.isArray(strain.images) && strain.images.find((i) => i.isMain)) ||
    (Array.isArray(strain.images) && strain.images[0]) ||
    null;
  const mainImage =
    (mainMedia?.type === 'video'
      ? mainMedia.thumbnailUrl || mainMedia.url
      : mainMedia?.url) ||
    strain.photo ||
    (strain.imageUrls && strain.imageUrls[0]) ||
    (publicView ? null : placeholder);
  const isVideoMain = mainMedia?.type === 'video';

  const baseQrUrl = (
    process.env.REACT_APP_STRAIN_QR_BASE_URL
    || process.env.REACT_APP_BASE_URL
    || `${window.location.origin}/strain`
  ).replace(/\/$/, '');
  const qrValue = strain.publicUrl || (strain.slug ? `${baseQrUrl}/${encodeURIComponent(strain.slug)}` : '');
  const showQrBlock = Boolean(qrValue);
  const showLabelLines = labelLines.some((line) => line);

  const handleCopy = async (event, value) => {
    event.preventDefault();
    if (event.stopPropagation) event.stopPropagation();
    if (!value) return;
    await copyText(value);
  };

  return (
    <div className="card h-100">
      {mainImage && (isVideoMain ? (
        <video
          src={mainMedia?.url}
          poster={mainImage}
          controls
          className="card-img-top"
          style={{ objectFit: 'cover', aspectRatio: '1/1' }}
        />
      ) : (
        <img
          src={mainImage}
          alt="preview"
          loading="lazy"
          className="card-img-top"
          style={{ objectFit: 'cover', aspectRatio: '1/1' }}
        />
      ))}
      <div className="card-body">
        <h5 className="card-title d-flex justify-content-between align-items-start">
          {displayNameValue && (
            <span className="d-inline-block text-truncate" style={{ maxWidth: "100%", wordBreak: "break-word", overflow: "hidden", textOverflow: "ellipsis" }} title={displayNameValue}>{displayNameValue}</span>
          )}
          {(strain.slug || (!publicView && strain._id)) && (
            <small className="text-muted">{strain.slug || strain._id}</small>
          )}
        </h5>
        {strain.genetics && <p className="mb-2"><small>{strain.genetics}</small></p>}
        {descriptionText && (
          <p className="card-text">
            {fullDesc ? descriptionText : shortDesc}{' '}
            {descriptionText.length > 120 && (
              <button
                className="btn btn-link btn-sm p-0"
                onClick={() => setFullDesc(!fullDesc)}
              >
                {fullDesc ? t('hide') : t('readMore')}
              </button>
            )}
          </p>
        )}
        {(showQrBlock || showLabelLines) && (
          <div className="d-flex flex-column align-items-center mb-3">
            {showQrBlock && <QRCode value={qrValue} size={96} />}
            {showLabelLines && (
              <div className="small text-center mt-2" style={{ wordBreak: "break-word" }}>
                {labelLines.map((line, idx) => (
                  <div key={idx}>{line}</div>
                ))}
              </div>
            )}
            {showQrBlock && (
              <div className="d-flex flex-wrap gap-2 justify-content-center mt-2 w-100">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  aria-label={t('copyQrText')}
                  onClick={(e) => handleCopy(e, qrValue)}
                >
                  {t('copyQrText')}
                </button>
                {labelText && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    aria-label={t('copyLabelText')}
                    onClick={(e) => handleCopy(e, labelText)}
                  >
                    {t('copyLabelText')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {isAdmin ? (
          <div className="mb-2">
            <button className="btn btn-sm btn-outline-primary me-1" onClick={() => onEdit && onEdit(strain)}>
              {t('edit')}
            </button>
            <button className="btn btn-sm btn-danger me-1" onClick={() => onDelete && onDelete(strain)}>
              {t('delete')}
            </button>
            <button className="btn btn-sm btn-outline-secondary me-1" onClick={addCutting}>
              {t('addCutting')}
            </button>
            <Link className="btn btn-sm btn-outline-secondary me-1" to={`/strain/${strain.slug}`}>{t('details')}</Link>
            <button className="btn btn-sm btn-outline-secondary" onClick={toggleExpand}>
              {expanded ? t('hideOffline') || 'Hide cuttings' : t('cuttings')}
            </button>
          </div>
        ) : (
          <Link className="btn btn-primary" to={`/strain/${strain.slug}`}>{t('viewMore')}</Link>
        )}
        {expanded && isAdmin && (
          <div className="mt-2">
            {loadingCuttings ? (
              <p>{t('loading')}</p>
            ) : cuttings.length === 0 ? (
              <p>{t('noCuttings') || 'No cuttings'}</p>
            ) : (
              <ul className="list-group list-group-flush">
                {cuttings.map((c) => (
                  <li
                    key={c._id}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <div className="d-flex align-items-center">
                      {(role === 'superuser' || role === 'admin') ? (
                        <img
                          src={c.photo || placeholder}
                          alt="preview"
                          loading="lazy"
                          className="me-2 img-thumbnail"
                          style={{ width: 150, height: 150, objectFit: 'cover' }}
                        />
                      ) : (
                        <img
                          src={placeholder}
                          alt="locked"
                          loading="lazy"
                          className="me-2 img-thumbnail"
                          style={{ width: 150, height: 150, objectFit: 'cover', filter: 'blur(4px)' }}
                        />
                      )}
                      <span>
                        <strong>{c.code}</strong> - {c.location || '-'} ({c.status || '-'})
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
